import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Loader2, Pencil, ChevronUp, Image as ImageIcon, Type, Minus, PanelTop } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OrderList } from "@/components/OrderList";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { BlockView } from "@/components/content/BlockView";
import { BlockEditor } from "@/components/content/BlockEditor";
import { api, errMsg } from "@/lib/api";
import { PAGES, BLOCK_TYPES, newBlock } from "@/lib/content";

const ICONS = { banner: PanelTop, text: Type, image: ImageIcon, divider: Minus };
const typeLabel = (t) => BLOCK_TYPES.find((x) => x.type === t)?.label;

function BlockRow({ b, open, onToggle, onChange, onRemove }) {
  const Icon = ICONS[b.type];
  return (
    <div>
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 shrink-0 text-[hsl(var(--gold))]" />
        <button type="button" onClick={onToggle} className="min-w-0 flex-1 text-left" data-testid={`block-open-${b.id}`}>
          <div className="truncate font-semibold text-slate-900">{b.title || typeLabel(b.type)}</div>
          <div className="text-xs text-slate-500">{typeLabel(b.type)}</div>
        </button>
        <OnOffSwitch checked={b.visible} onChange={(v) => onChange({ visible: v })} testid={`block-visible-${b.id}`} />
        <Button variant="ghost" size="icon" onClick={onToggle} data-testid={`block-edit-${b.id}`}>{open ? <ChevronUp className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}</Button>
        <ConfirmDelete onConfirm={onRemove} title="Remove this section?" message="It will be removed when you press Save." testid={`block-delete-${b.id}`} />
      </div>
      {open && <BlockEditor block={b} onChange={onChange} />}
    </div>
  );
}

export default function AdminContent() {
  const [page, setPage] = useState("front");
  const [blocks, setBlocks] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/content/${page}`).then((r) => { setBlocks(r.data.blocks); setDirty(false); setOpenId(null); }).catch((e) => toast.error(errMsg(e)));
  }, [page]);

  const change = (next) => { setBlocks(next); setDirty(true); };
  const update = (id, patch) => change(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const add = (type) => { const b = newBlock(type); change([...blocks, b]); setOpenId(b.id); };
  const switchPage = (k) => { if (!dirty || window.confirm("You have unsaved changes. Leave without saving?")) setPage(k); };
  const save = async () => {
    setSaving(true);
    try { const r = await api.put(`/content/${page}`, { blocks }); setBlocks(r.data.blocks); setDirty(false); toast.success("Page saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const visible = blocks.filter((b) => b.visible);
  const isFront = page === "front";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2" data-testid="content-page-picker">
        {PAGES.map((p) => (
          <button key={p.key} onClick={() => switchPage(p.key)} data-testid={`content-page-${p.key}`}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${p.key === page ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`}>{p.label}</button>
        ))}
      </div>
      <div className="sticky top-[68px] z-20 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
        <span className="mr-1 text-sm font-semibold text-slate-600">Add:</span>
        {BLOCK_TYPES.map((t) => (
          <Button key={t.type} variant="outline" size="sm" onClick={() => add(t.type)} data-testid={`content-add-${t.type}`}><Plus className="mr-1 h-4 w-4" />{t.label}</Button>
        ))}
        <span className="flex-1" />
        {dirty && <span className="text-sm font-semibold text-amber-700" data-testid="content-unsaved">Unsaved changes</span>}
        <Button onClick={save} disabled={saving || !dirty} data-testid="content-save-btn">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save page</Button>
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-2">
          <p className="text-on-bg-muted text-sm">Drag the handle (or use the arrows) to move sections and dividers. Tap a section to edit its words, font, colors and picture.</p>
          <OrderList items={blocks} testid="block" empty="No sections on this page yet. Use “Add” above."
            onSave={(ids) => change(ids.map((id) => blocks.find((b) => b.id === id)))}
            renderItem={(b) => <BlockRow b={b} open={openId === b.id} onToggle={() => setOpenId(openId === b.id ? null : b.id)} onChange={(p) => update(b.id, p)} onRemove={() => change(blocks.filter((x) => x.id !== b.id))} />} />
        </div>
        <div className="xl:sticky xl:top-[140px] xl:self-start">
          <div className="text-on-bg-muted mb-2 text-xs font-semibold uppercase tracking-[0.18em]">Live preview</div>
          <div className={`overflow-hidden rounded-xl border border-white/20 bg-background ${isFront ? "flex h-[560px] flex-col" : "space-y-4 p-4"}`} data-testid="content-preview">
            {!visible.length && <p className="p-10 text-center text-sm text-slate-500">Nothing to show yet.</p>}
            {visible.map((b) => <BlockView key={b.id} block={b} fill={isFront} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
